import { Injectable, WritableSignal, signal } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { ApiGatewayModel } from '../models/api-gateway-model';
import { ApiGatewayService } from '../shared/services/api-gateway.service';
import { MsalService } from '@azure/msal-angular';
import { AdminGetEmployeesDto, CompanyModel, CustomerServiceDto, Employee } from './customer-service-model';
import { AdminAuthService } from '../shared/services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class CustomerServicesService {
  constructor(private apiService: ApiGatewayService, private authService: AdminAuthService) {}

  canImpersonateSignal = signal<boolean>(this.authService.hasUserRole('hasimpersonation'));
  public allActiveEmployeesCount: WritableSignal<number> = signal(0);

  public setAllActiveEmployeesCount(companyId: string): void {
    this.adminGetEmployees(companyId, 10, 1, 'Active').subscribe();
  }

  public getAllEmployeesFromAllCompanies(pagesize: number, page: number, searchTerm?: string, searchByFilter?: string): Observable<CustomerServiceDto> {
    let gatewayModel: ApiGatewayModel = {
      actionDescription: 'AdminGetEmployees',
      body: { PageSize: pagesize, PageNumber: page, SearchTerm: searchTerm, searchByFilter: searchByFilter },
    };

    return this.apiService.SendRequest('', gatewayModel).pipe(map((x) => x[0]));
  }

  public loginAs(user: string, companyId: string): Observable<any> {
    let gatewayModel: ApiGatewayModel = {
      actionDescription: 'AdminLoginAs',
      body: {
        email: user,
        companyId: companyId,
        adToken: this.authService.getAuthToken(),
      },
    };
    return this.apiService.SendRequest('', gatewayModel).pipe(map((x) => x[0]));
  }

  public GetAdminCompany(companyId: string): Observable<CompanyModel> {
    let gatewayModel: ApiGatewayModel = {
      actionDescription: 'GetAdminCompany',
      body: {},
    };
    let queryString = '?CompanyId=' + companyId;
    return this.apiService.SendRequest(queryString, gatewayModel).pipe(map((x) => x[0]));
  }

  public updateAdminCompanyDetails(companyId: string, companyDetails: CompanyModel): Observable<CompanyModel> {
    let gatewayModel: ApiGatewayModel = {
      actionDescription: 'UpdateAdminCompanyDetails',
      body: companyDetails,
    };
    let queryString = '?CompanyId=' + companyId;
    return this.apiService.SendRequest(queryString, gatewayModel).pipe(map((x) => x[0]));
  }

  public updateCompanyOwner(companyId: string, oldOwnerEmployeeId:string, newOwnerEmployeeId: string): Observable<any> {
    let gatewayModel: ApiGatewayModel = {
      actionDescription: 'UpdateCompanyOwner',
      body: {},
    };

    const params = new URLSearchParams({
      companyId: companyId,
      oldOwnerEmployeeId: oldOwnerEmployeeId,
      newOwnerEmployeeId: newOwnerEmployeeId,
    });

    let queryString = "?" + params.toString();

    return this.apiService.SendRequest(queryString, gatewayModel).pipe(map((x) => x[0]));
  }

  public adminGetEmployees(companyId: string, pageSize: number = 10, pageNumber = 1, statusFilter?: string, searchTerm?: string, searchByFilter?: string): Observable<AdminGetEmployeesDto> {
    let gatewayModel: ApiGatewayModel = {
      actionDescription: 'AdminGetEmployees',
      body: {
        pageSize,
        pageNumber,
        companyId,
        OrderByOwner: true,
        StatusFilter: statusFilter,
        SearchTerm: searchTerm,
        SearchByFilter: searchByFilter
      },
    };
    let queryString = '?CompanyId=' + companyId;
    return this.apiService.SendRequest(queryString, gatewayModel).pipe(
      map((x) => x[0]),
      tap((y) => this.allActiveEmployeesCount.set(y.pagingData?.allActiveEmployeesCount ?? 0))
    );
  }

  public adminGetEmployeeList(companyId: string, pageSize: number = 10, pageNumber = 1, statusFilter?: string): Observable<AdminGetEmployeesDto> {
    let gatewayModel: ApiGatewayModel ={
      actionDescription: "AdminGetEmployeeList",
      companyId: companyId,
      body: {},

    };

    const params = new URLSearchParams({
      companyId: companyId,
      pageSize: pageSize.toString(),
      pageNumber: pageNumber.toString(),
      statusFilter: statusFilter || ''
    });

    let queryString = "?" + params.toString();

    return this.apiService.SendRequest(queryString, gatewayModel).pipe(map((x) => x[0]));
  }
}
